import {Button} from '@wandb/components';
import React, {useEffect, useMemo, useState} from 'react';

import {maybePluralize} from '../../../util/uihelpers';
import {BULK_ADD_LIMIT_MULTI_METRIC} from './constants';
import {type MultiMetricPanelsMap} from './hooks/useMultiMetricPanelsMap';
import {MultiMetricPanelsListItem} from './MultiMetricPanelsListItem';

/** Show up to this many items before collapsing the list */
const COLLAPSIBLE_THRESHOLD = 10;
/**
 * Show this many items when the list is collapsed.
 *
 * Subtracting from COLLAPSIBLE_THRESHOLD ensures there's at least
 * that many more items to show. If the difference is too small,
 * the UX would feel unnatural (e.g. if only 1 more item is shown
 * when expanded, feels like it didn't really need to be collapsed).
 */
const COLLAPSED_PAGE_SIZE = COLLAPSIBLE_THRESHOLD - 4;

type MultiMetricPanelsListProps = {
  panelsToAdd: MultiMetricPanelsMap;
  numNotAdded: number;
  numAlreadyAdded: number;
  isPanelLimitExceeded: boolean;
};

export const MultiMetricPanelsList = ({
  panelsToAdd,
  numNotAdded,
  numAlreadyAdded,
  isPanelLimitExceeded,
}: MultiMetricPanelsListProps) => {
  // Memoizing because panelsToAdd size could get quite large
  const panelRegexes = useMemo(() => {
    return Array.from(panelsToAdd.keys()).sort((a, b) => {
      // sort panels that have already been added to bottom of list
      const aAdded = panelsToAdd.get(a)?.isAlreadyAdded ?? false;
      const bAdded = panelsToAdd.get(b)?.isAlreadyAdded ?? false;
      return aAdded ? 1 : bAdded ? -1 : 0;
    });
  }, [panelsToAdd]);

  const isCollapsible = panelRegexes.length > COLLAPSIBLE_THRESHOLD;
  const [isCollapsed, setIsCollapsed] = useState(isCollapsible);
  useEffect(() => {
    // Reset collapsed state if listed panels change
    setIsCollapsed(panelRegexes.length > COLLAPSIBLE_THRESHOLD);
  }, [panelRegexes.length]);

  const displayedRegexes = useMemo(() => {
    if (isCollapsed) {
      return panelRegexes.slice(0, COLLAPSED_PAGE_SIZE);
    }
    if (isPanelLimitExceeded) {
      return panelRegexes.slice(0, BULK_ADD_LIMIT_MULTI_METRIC);
    }
    return panelRegexes;
  }, [panelRegexes, isCollapsed, isPanelLimitExceeded]);

  let title;
  if (numNotAdded === 0) {
    title = 'No panels to add';
  } else if (isPanelLimitExceeded) {
    title = `Over ${BULK_ADD_LIMIT_MULTI_METRIC} panels`;
  } else {
    title = `${maybePluralize(numNotAdded, 'panel')} will be added`;
  }

  return (
    <div>
      <p className="text-wb-text-primary font-semibold mb-8">
        {title}
        {numAlreadyAdded > 0 && (
          <span className="text-wb-text-tertiary font-normal">
            {' '}
            ({maybePluralize(numAlreadyAdded, 'panel')} already in workspace)
          </span>
        )}
      </p>
      <div className="ml-12 border-l-2 border-solid border-wb-border pl-12">
        <div className="flex flex-col gap-8">
          {displayedRegexes.map(panelRegex => {
            const panelToAdd = panelsToAdd.get(panelRegex);
            if (!panelToAdd) {
              return null;
            }
            return (
              <MultiMetricPanelsListItem
                key={panelRegex}
                regex={panelRegex}
                defaultSection={panelToAdd.defaultSection}
                metrics={panelToAdd.metrics}
                isAlreadyAdded={panelToAdd.isAlreadyAdded}
              />
            );
          })}
          {isPanelLimitExceeded && !isCollapsed && (
            <span className="text-sm text-wb-text-tertiary">
              ... and{' '}
              {(
                panelRegexes.length - BULK_ADD_LIMIT_MULTI_METRIC
              ).toLocaleString()}{' '}
              more
            </span>
          )}
          {isCollapsible && (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => setIsCollapsed(prev => !prev)}>
              {isCollapsed ? 'Show all' : 'Show less'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
